// Trip endpoints for the two companies that share the Hawaii bus. GoJump
// trips live on booking-server, reached through booking-app-api's /driver/*
// relay (Keycloak bearer; bare JSON on GETs, a numeric locationId in every
// path, pickups addressed by display name); GoSky Hawaii trips live on the
// GoSky admin (/v1/shuttle/*, {status, data} envelope, one implicit location,
// pickups addressed by product key). The Trips screen shows one list: GoJump
// everywhere, plus GoSky merged in when the location is Hawaii.

export const HAWAII = 5;

function gojumpBase() {
  return process.env.REACT_APP_BOOKING_APP_API_URL;
}
function goskyBase() {
  return (process.env.REACT_APP_GOSKY_SERVER_URL || '').replace(/\/+$/, '');
}

// GoSky rides along only in Hawaii, and only when a GoSky server is configured.
export function hasGoSky(locationId) {
  return Number(locationId) === HAWAII && goskyBase() !== '';
}

export const gojump = {
  tripsUrl: (date, locationId) => `${gojumpBase()}/driver/trips/${date}/${locationId}`,
  detailsUrl: (date, locationId, pickupSpot, hour, minute) =>
    `${gojumpBase()}/driver/trips/${date}/${locationId}/${pickupSpot}/${hour}/${minute}`,
  saveUrl: (date, locationId, pickupSpot, hour, minute, status) =>
    `${gojumpBase()}/driver/trips/${date}/${locationId}/${pickupSpot}/${hour}/${minute}/${status}`,
  saveBody: (rows) => rows,
};

export const gosky = {
  tripsUrl: (date) => `${goskyBase()}/v1/shuttle/trips?date=${encodeURIComponent(date)}`,
  detailsUrl: (date, pickupKey, hour, minute) =>
    `${goskyBase()}/v1/shuttle/trips/${date}/${encodeURIComponent(pickupKey)}/${hour}/${minute}`,
  saveUrl: (date, pickupKey, hour, minute, status) =>
    `${goskyBase()}/v1/shuttle/trips/${date}/${encodeURIComponent(pickupKey)}/${hour}/${minute}/${status}`,
  saveBody: (rows) => ({ bookings: rows }),
};

// The response body as the screens consume it — GoJump GETs (booking-app-api
// relaying booking-server) answer with the array itself, the GoSky admin wraps
// it in {status:'success', data}; booking-app-api also wraps its POST replies,
// which the screens don't read.
// Detected by shape: axios-hooks keeps the previous body around while the
// next URL loads, so a stale body can belong to either server.
export function unwrap(data) {
  return data && typeof data === 'object' && !Array.isArray(data) && 'data' in data ? data.data : data;
}

export function asList(data) {
  const body = unwrap(data);
  return Array.isArray(body) ? body : [];
}

// Both companies name a pickup by the hotel, GoJump and GoSky alike with an
// optional "(Return)" suffix; a trip is the hotel at a departure time.
function tripKey(pickupSpot, hour, minute) {
  const spot = String(pickupSpot || '')
    .replace(/\s*\(Return\)/i, '')
    .trim()
    .toLowerCase();
  return `${spot}|${Number(hour)}|${Number(minute)}`;
}

// One row per (hotel, time) across both companies. A row keeps the GoJump
// shape the screens already render, plus: `gojump` (a GoJump trip exists),
// `pickupKey` (the GoSky pickup, when GoSky riders are on it) and
// `goskyPeople`. A merged trip is complete only once both sides are.
export function mergeTrips(gojumpTrips, goskyTrips) {
  const rows = new Map();
  gojumpTrips.forEach((trip) => {
    rows.set(tripKey(trip.pickupSpot, trip.pickupTime.hour, trip.pickupTime.minute), {
      ...trip,
      gojump: true,
      pickupKey: null,
      goskyPeople: 0,
    });
  });
  goskyTrips.forEach((trip) => {
    const key = tripKey(trip.pickupSpot, trip.pickupTime.hour, trip.pickupTime.minute);
    const row = rows.get(key);
    if (!row) {
      rows.set(key, { ...trip, gojump: false, goskyPeople: trip.numberOfPeople });
      return;
    }
    rows.set(key, {
      ...row,
      pickupKey: trip.pickupKey,
      goskyPeople: trip.numberOfPeople,
      numberOfPeople: row.numberOfPeople + trip.numberOfPeople,
      tripStatus: row.tripStatus === 1 && trip.tripStatus === 1 ? 1 : 0,
      driver: row.driver || trip.driver,
    });
  });
  return Array.from(rows.values());
}

// The trip-details route: GoJump's original path, untouched, plus a query
// string that says which backends the trip spans — `gosky=<pickupKey>` when
// GoSky riders are on it, `gojump=0` when no GoJump trip exists for it.
export function tripDetailsPath(date, locationId, trip) {
  const { pickupSpot, pickupTime, tripStatus, driver, gojump: hasGoJump, pickupKey } = trip;
  const path = `/tripdetails/${date}/${locationId}/${pickupSpot}/${pickupTime.hour}/${pickupTime.minute}/${tripStatus}/${driver || 'none'}`;
  const query = new URLSearchParams();
  if (pickupKey) query.set('gosky', pickupKey);
  if (!hasGoJump) query.set('gojump', '0');
  const search = query.toString();
  return search ? `${path}?${search}` : path;
}
